import { db } from "services/firebase";

function overrideDocId(level, scopeId, deckId) {
  return `${level}__${scopeId}__${deckId}`;
}

export async function loadBaseDeck(deckId) {
  const snap = await db.collection("trainingDecks").doc(deckId).get();
  return snap.exists ? { id: snap.id, ...snap.data() } : null;
}

export async function loadDeckOverride(level, scopeId, deckId) {
  try {
    const snap = await db
      .collection("trainingDeckOverrides")
      .doc(overrideDocId(level, scopeId, deckId))
      .get();
    return snap.exists ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.warn("[trainingDeckService] override load skipped", { level, scopeId, deckId, err });
    return null;
  }
}

/**
 * Compose base deck + hierarchy overrides (most specific wins per slideId).
 */
export async function composeDeck(deckId, userHierarchyPath = {}) {
  const base = await loadBaseDeck(deckId);
  if (!base) return [];

  const slideMap = new Map();
  (base.slides || []).forEach((slide) => slideMap.set(slide.slideId, { ...slide }));

  const layers = [
    userHierarchyPath.regionId ? { level: 2, scopeId: userHierarchyPath.regionId } : null,
    userHierarchyPath.institutionId ? { level: 3, scopeId: userHierarchyPath.institutionId } : null,
    userHierarchyPath.subInstitutionId
      ? { level: 4, scopeId: userHierarchyPath.subInstitutionId }
      : null,
    userHierarchyPath.departmentId ? { level: 5, scopeId: userHierarchyPath.departmentId } : null,
    userHierarchyPath.subDepartmentId
      ? { level: 6, scopeId: userHierarchyPath.subDepartmentId }
      : null,
  ].filter(Boolean);

  let suppressed = new Set();
  for (const layer of layers) {
    const override = await loadDeckOverride(layer.level, layer.scopeId, deckId);
    if (!override) continue;
    (override.slides || []).forEach((slide) => slideMap.set(slide.slideId, { ...slide }));
    if (Array.isArray(override.suppressedSlideIds)) {
      suppressed = new Set(override.suppressedSlideIds);
    }
  }

  return [...slideMap.values()]
    .filter((slide) => !suppressed.has(slide.slideId))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export async function saveDeckOverride({ level, scopeId, deckId, institutionId, slides, suppressedSlideIds }) {
  const ref = db.collection("trainingDeckOverrides").doc(overrideDocId(level, scopeId, deckId));
  await ref.set(
    {
      level,
      scopeId,
      deckId,
      institutionId: institutionId || null,
      slides: slides || [],
      suppressedSlideIds: suppressedSlideIds || [],
      updatedAt: new Date(),
    },
    { merge: true },
  );
}

export const DECK_BY_ROLE = {
  "fde-c": "fde-c-orientation",
  "fde-dc": "tci-manager-orientation",
  "fde-t": "fde-t-orientation",
  admin: "institution-leader-orientation",
};

/**
 * Module 4 TCI-specific deck templates (Decision 1.6).
 * Institutions override via trainingDeckOverrides; ASI seeds base templates.
 */
export const MODULE_4_DECK_BY_TCI = {
  triage: "fde-c-module4-triage",
  hpi: "fde-c-module4-hpi",
  medrec: "fde-c-module4-medrec",
  "medrec-intake": "fde-c-module4-medrec-intake",
  differential: "fde-c-module4-differential",
};

export function module4DeckIdForTci(tciId) {
  return MODULE_4_DECK_BY_TCI[tciId] || `fde-c-module4-${tciId}`;
}

export async function composeModule4Deck(tciId, userHierarchyPath = {}) {
  const deckId = module4DeckIdForTci(tciId);
  const slides = await composeDeck(deckId, userHierarchyPath);
  if (slides.length) return { deckId, slides, isTemplate: false };

  // Fallback template when base deck not yet seeded — keeps onboarding unblocked.
  return {
    deckId,
    isTemplate: true,
    slides: [
      {
        slideId: "m4-intro",
        order: 1,
        title: `Module 4 — ${tciId}`,
        body: "Institution-specific clinical standards for this TCI. Your TCI Manager will complete this deck.",
        dependsOn: [],
      },
      {
        slideId: "m4-must-do",
        order: 2,
        title: "Must-do behaviors",
        body: "Review the governance must-do list for this TCI before annotating live sessions.",
        dependsOn: [],
      },
      {
        slideId: "m4-must-not",
        order: 3,
        title: "Must-not-do behaviors",
        body: "Flag any must-not-do violations during session annotation.",
        dependsOn: [],
      },
    ],
  };
}
